import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./offerform.scss";
import OfferFormLayout from "./OfferFormLayout";
import CategorySelection from "./CategorySelection";
import SubCategorySelection from "./SubCategorySelection";
import OfferCreation from "./OfferCreation";
import CustomizeOffer from "./CustomizeOffer";
import RulesRegulations from "./RulesRegulations";
import Location from "./Location";
import OfferReview from "./OfferReview";
import MerchantSelection from "./MerchantSelection";
import GenericPopup from "../GenericPopup";
import ThreeActionPopup from "../ThreeActionPopup";
import PrimaryOutlineButton from "../PrimaryOutlineButton";
import PrimaryButton from "../PrimaryButton";
import InfoIcon from "../../../Assets/Icons/info.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { store } from "../../../redux/store";
import {
  createOffer,
  getCategories,
  getOfferById,
} from "../../../services/offerService";
import { resetOfferState } from "../../../redux/slices/offerSlice";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import { logout } from "../../../services/authService";
import { getFcmToken } from "../../../utils/connectStore";
import toast from "../../../utils/toast";
import {
  getOfferExitBehavior,
  getOfferEditProgressSaveFlags,
  isLiveOfferStatus,
} from "./offerApprovalHelpers";

const OfferForm = ({ merchant }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id = null, merchantId: routeMerchantId = null } =
    location?.state || "";

  // Use local state instead of useSelector to avoid reducer execution conflicts
  const [role, setRole] = useState(null);
  const [offerData, setOfferData] = useState(null);

  // Determine initial step based on role
  const [stepIndex, setStepIndex] = useState(1);
  const [subCategories, setSubCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(undefined);
  const [payload, setPayload] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editOfferData, setEditOfferData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null); // For admin merchant selection
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showExitResubmitConfirm, setShowExitResubmitConfirm] = useState(false);
  const [isExitResubmitting, setIsExitResubmitting] = useState(false);
  const [exitValidationIssue, setExitValidationIssue] = useState(null);
  const [pendingNavigatePath, setPendingNavigatePath] = useState(null);
  const [pendingAction, setPendingAction] = useState("navigate");
  const [showAdminSaveSuccessModal, setShowAdminSaveSuccessModal] =
    useState(false);
  const [adminSaveSuccessMessage, setAdminSaveSuccessMessage] = useState("");
  const [isAdminSaveAndExitSubmitting, setIsAdminSaveAndExitSubmitting] =
    useState(false);
  const [isMerchantExitSubmitting, setIsMerchantExitSubmitting] =
    useState(false);
  const topRef = useRef(null);

  // Safely update state from Redux store to avoid reducer execution conflicts
  useEffect(() => {
    const updateState = () => {
      try {
        const state = store.getState();
        const newRole = state?.authReducer?.tokenData?.user?.role;
        const newOfferData = state?.offerReducer?.createdOffer;
        setRole(newRole);
        setOfferData(newOfferData);
      } catch (error) {
        console.warn("OfferForm store access error:", error.message);
      }
    };

    // Initial state update with delay to avoid reducer execution conflicts
    const timeoutId = setTimeout(updateState, 0);
    return () => clearTimeout(timeoutId);
  }, []);

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      try {
        const state = store.getState();
        const newRole = state?.authReducer?.tokenData?.user?.role;
        const newOfferData = state?.offerReducer?.createdOffer;
        setRole((prev) => {
          if (prev !== newRole) return newRole;
          return prev;
        });
        setOfferData((prev) => {
          if (prev !== newOfferData) return newOfferData;
          return prev;
        });
      } catch (error) {
        console.warn("OfferForm store subscription error:", error.message);
      }
    });

    return unsubscribe;
  }, []);

  // Helper function to get step numbers based on role
  const getStepNumber = (baseStep) => {
    return role === "admin" ? baseStep + 1 : baseStep;
  };

  const getOfferData = async () => {
    try {
      const getPromise = await dispatch(
        getOfferById({ id, merchantId: routeMerchantId }),
      )?.unwrap();
      if (getPromise?.success === true) {
        setEditOfferData(getPromise?.data);
      }
    } catch (error) {
      const message =
        error?.message ||
        error?.data?.message ||
        error?.response?.data?.message ||
        "Failed to load offer details.";
      toast.error(message);
      navigate(offersPath);
    }
  };

  const getCategoriesData = async (merchantId = null) => {
    try {
      const categoryPromise = await dispatch(
        getCategories(merchantId ? { merchantId } : {}),
      )?.unwrap();
      if (categoryPromise?.success === true) {
        setCategories(categoryPromise?.data);
      }
    } catch (error) {
      const message =
        error?.message ||
        error?.data?.message ||
        error?.response?.data?.message ||
        "Failed to load categories.";
      toast.error(message);
    }
  };

  const effectiveMerchantIdForCategories =
    routeMerchantId ||
    selectedMerchant?._id ||
    editOfferData?.merchant?._id ||
    editOfferData?.merchantId ||
    (typeof editOfferData?.merchant === "string"
      ? editOfferData.merchant
      : null) ||
    null;

  useEffect(() => {
    getCategoriesData(effectiveMerchantIdForCategories);
  }, [effectiveMerchantIdForCategories]);

  useEffect(() => {
    if (id) {
      setIsEdit(true);
      getOfferData();
    } else {
      setIsEdit(false);
      setEditOfferData(null);
      // For a brand new offer, we must clear any leftover draft data from Redux
      dispatch(resetOfferState());
    }
  }, [id, dispatch]);

  // Handle resumption logic when editing an existing offer
  const [initialLiveStatus, setInitialLiveStatus] = useState(null);

  const currentStatus = (
    editOfferData?.status ||
    offerData?.status ||
    ""
  ).toLowerCase();
  const wasOriginallyLive = isLiveOfferStatus(initialLiveStatus);
  const isApproved = wasOriginallyLive || isLiveOfferStatus(currentStatus);

  useEffect(() => {
    if (editOfferData?.status && initialLiveStatus === null) {
      setInitialLiveStatus(editOfferData.status.toLowerCase());
    }
  }, [editOfferData, initialLiveStatus]);

  useEffect(() => {
    if (isEdit && editOfferData && role) {
      const status = (editOfferData.status || "").toLowerCase();

      // Helper to find the first incomplete base step (1-7)
      const findResumptionStep = (data) => {
        // 1. Category Selection
        const hasCategory = !!data.category;
        if (!hasCategory) return 1;

        // 2. Sub Category Selection
        // Subcategories might be an array or a single value depending on implementation
        const subs = data.subCategory;
        const hasSubCategory = Array.isArray(subs) ? subs.length > 0 : !!subs;
        if (!hasSubCategory) return 2;

        // 3. Offer Creation (About Offer)
        // Strictly require Title, Thumbnail, and Discount Type
        const hasTitle = data.title && String(data.title).trim() !== "";
        const hasThumbnail = !!data.thumbnailImage;
        const hasDiscountType = !!data.offerDiscountType;

        if (!hasTitle || !hasThumbnail || !hasDiscountType) return 3;

        // 4. Customisation
        // Require Redemption Method and a basic rule/frequency setup
        const hasRedemptionMethod = !!data.redemptionMethod;
        const hasNecessaryRule = !!data.necessaryRule;
        // Frequency is expected unless it's a date range
        const hasFrequency =
          !!data.redemptionFrequency ||
          data.necessaryRule === "CHOOSE_DATE_RANGE";

        if (!hasRedemptionMethod || !hasNecessaryRule || !hasFrequency)
          return 4;

        // 5. Rules and Regulations
        const rules = Array.isArray(data.ruleAndRegulation)
          ? data.ruleAndRegulation
          : [];
        if (rules.length === 0) return 5;

        // 6. Location (Only for INSTORE offers)
        if (
          data.offerType === "INSTORE_SWOODLE_OFFER" ||
          data.offerType === "INSTORE_OFFER"
        ) {
          const locs = Array.isArray(data.locations) ? data.locations : [];
          const tempLocs = Array.isArray(data.tempLocations)
            ? data.tempLocations
            : [];
          const isAllStores = !!data.availableAcrossAllStores;

          if (!isAllStores && locs.length === 0 && tempLocs.length === 0)
            return 6;
        }

        // 7. Review and Save
        return data.offerType === "INSTORE_SWOODLE_OFFER" ||
          data.offerType === "INSTORE_OFFER"
          ? 7
          : 6;
      };

      // Case A: Non-Draft (Pending, Approved, Declined, etc.)
      // Always start from the very beginning for review
      if (status && status !== "drafted") {
        const resetStep = role === "admin" ? 2 : 1;
        if (stepIndex !== resetStep) {
          console.log(
            `[Resumption] Status is '${status}'. Resetting to review-start Step ${resetStep}.`,
          );
          setStepIndex(resetStep);
        }
        return;
      }

      // Case B: Drafted offers
      // Go to the exact step where the user actually left off
      // Only trigger this if we are still at the initial/first screen
      if (stepIndex === 1 || (role === "admin" && stepIndex === 2)) {
        const baseStep = findResumptionStep(editOfferData);
        const resumedStep = getStepNumber(baseStep);

        if (resumedStep !== stepIndex) {
          console.log(
            `[Resumption] Draft detected. Resuming at Base Step ${baseStep} (Layout Step ${resumedStep})`,
          );
          setStepIndex(resumedStep);
        }
      }
    }
  }, [isEdit, editOfferData, role]);

  // Auto-populate selectedMerchant when admin edits an existing offer.
  // MerchantSelection is never rendered for isEdit=true, so selectedMerchant
  // stays null unless we seed it from the offer's embedded merchant data.
  // Without this, Location's getFranchises call has no merchantId → 400 error.
  useEffect(() => {
    if (
      isEdit &&
      editOfferData?.merchant &&
      role === "admin" &&
      !selectedMerchant
    ) {
      const merchant = editOfferData.merchant;
      setSelectedMerchant(merchant);
      // Also update Redux so getFranchises (which reads selectedMerchent from store) works correctly
      dispatch(setCurrentBusiness(merchant));
      console.log(
        "[EditOffer] Auto-populated selectedMerchant from editOfferData:",
        merchant?.businessName,
      );
    }
  }, [isEdit, editOfferData, role, selectedMerchant, dispatch]);

  // Load subcategories when editing and resuming directly at Subcategory step
  useEffect(() => {
    if (isEdit && editOfferData && categories.length > 0) {
      const getCategoryId = (value) =>
        typeof value === "object" && value !== null
          ? value._id || value.id
          : value;

      // Prefer the in-flight payload from the current edit session. If the user
      // changed category, payload.category is the source of truth.
      const activeCategoryId = getCategoryId(payload?.category);
      const fallbackCategoryId = getCategoryId(editOfferData.category);
      const resolvedCategoryId = activeCategoryId || fallbackCategoryId;

      if (resolvedCategoryId && resolvedCategoryId !== categoryId) {
        setCategoryId(resolvedCategoryId);
      }

      const resolvedCategory = categories.find(
        (cat) => cat._id === resolvedCategoryId,
      );
      if (resolvedCategory && Array.isArray(resolvedCategory.subCategory)) {
        setSubCategories(resolvedCategory.subCategory);
      }
    }
  }, [
    isEdit,
    editOfferData,
    categories,
    categoryId,
    payload?.category,
    setCategoryId,
    setSubCategories,
  ]);

  const shouldHoldAdminEditBootstrap =
    isEdit && role === "admin" && !!id && !editOfferData;

  const handleStepClick = (target) => {
    setStepIndex(target);
  };

  const offersPath =
    role === "admin" ? "/admin/offer-list" : "/merchant/offer-list";
  const isAdminRole = role === "admin" || role === "ADMIN";
  const roleLower = String(role || "").toLowerCase();
  const isMerchantRole = roleLower === "merchant";
  const currentEditStatus = String(
    editOfferData?.status || offerData?.status || "",
  ).toLowerCase();
  const shouldShowMerchantBrowserBackProcessing =
    isMerchantRole &&
    Boolean(isEdit) &&
    (
      currentEditStatus === "live" ||
      currentEditStatus === "approved" ||
      currentEditStatus === "pending" ||
      currentEditStatus === "disabled" ||
      currentEditStatus === "declined" ||
      currentEditStatus === "rejected"
    );

  const handlePrevious = () => {
    let previousStep = stepIndex - 1;
    if (previousStep < 1) {
      previousStep = 1;
    }
    setStepIndex(previousStep);
  };

  const setPayloadData = useCallback((data) => {
    if (data === null) {
      setPayload(null);
    } else {
      const sanitizedData = Object.entries(data || {}).reduce(
        (acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value;
          }
          return acc;
        },
        {},
      );

      setPayload((prev) => ({ ...(prev || {}), ...sanitizedData }));
    }
  }, []);

  const isLeaveSaveStep = stepIndex >= getStepNumber(3);

  const clearExitValidationIssue = useCallback(() => {
    setExitValidationIssue(null);
  }, []);

  useEffect(() => {
    if (!exitValidationIssue) return;

    setShowLeaveConfirm(false);
    setShowExitResubmitConfirm(false);
    setIsExitResubmitting(false);
    setPendingNavigatePath(null);
    setPendingAction("navigate");
  }, [exitValidationIssue]);

  const getExitValidationIssue = useCallback(
    (rawMessage) => {
      const message = String(rawMessage || "").trim();
      const normalized = message.toLowerCase();
      const locationStep = getStepNumber(6);

      if (!message) return null;

      if (
        normalized.includes("category is required") &&
        !normalized.includes("subcategory")
      ) {
        return {
          stepIndex: getStepNumber(1),
          field: "category",
          message,
        };
      }

      if (normalized.includes("subcategory")) {
        return {
          stepIndex: getStepNumber(2),
          field: "subCategory",
          message,
        };
      }

      if (
        normalized.includes("offer title") ||
        normalized.includes("discount type") ||
        normalized.includes("thumbnail") ||
        normalized.includes("preview image") ||
        normalized.includes("website url") ||
        normalized.includes("online offer subtype") ||
        normalized.includes("offer code") ||
        normalized.includes("free offer details") ||
        normalized.includes("bogo details") ||
        normalized.includes("bundle details") ||
        normalized.includes("description contains") ||
        normalized.includes("offer description")
      ) {
        let field = "title";

        if (normalized.includes("discount type")) field = "offerDiscountType";
        else if (normalized.includes("thumbnail")) field = "thumbnailImage";
        else if (normalized.includes("preview image")) field = "previewImages";
        else if (normalized.includes("website url")) field = "websiteURL";
        else if (normalized.includes("online offer subtype"))
          field = "onlineOption";
        else if (normalized.includes("offer code")) field = "offerCodes";
        else if (normalized.includes("free offer details"))
          field = "freeOfferDetails";
        else if (normalized.includes("bogo details")) field = "bogoDetails";
        else if (normalized.includes("bundle details"))
          field = "bundleOfferDetails";
        else if (normalized.includes("description")) field = "description";

        return {
          stepIndex: getStepNumber(3),
          field,
          message,
        };
      }

      // B5 fix: check 'redemption method' specifically BEFORE the general 'redemption'
      // check so we route the user to the right field on Step 4.
      if (normalized.includes("redemption method")) {
        return {
          stepIndex: getStepNumber(4),
          field: "redemptionMethod",
          message,
        };
      }

      if (
        normalized.includes("redemption") ||
        normalized.includes("necessary rule") ||
        normalized.includes("start date") ||
        normalized.includes("end date") ||
        normalized.includes("start time") ||
        normalized.includes("end time") ||
        normalized.includes("callback url")
      ) {
        let field = "rules";

        if (normalized.includes("start date")) field = "startDate";
        else if (normalized.includes("end date")) field = "endDate";
        else if (normalized.includes("start time")) field = "startTime";
        else if (normalized.includes("end time")) field = "endTime";
        else if (normalized.includes("callback url")) field = "url";
        else if (normalized.includes("redemption day")) field = "days";

        return {
          stepIndex: getStepNumber(4),
          field,
          message,
        };
      }

      if (
        normalized.includes("rules and regulations") ||
        normalized.includes("predefined rules") ||
        normalized.includes("invalid predefined")
      ) {
        return {
          stepIndex: getStepNumber(5),
          field: "ruleAndRegulation",
          message,
        };
      }

      if (
        normalized.includes("location") ||
        normalized.includes("stores") ||
        normalized.includes("availableacrossallstores")
      ) {
        return {
          stepIndex: locationStep,
          field: "location",
          message,
        };
      }

      return null;
    },
    [getStepNumber],
  );

  const completePendingExit = useCallback(async () => {
    sessionStorage.removeItem("ADMIN_OFFER_CREATION_GUARD");

    if (pendingAction === "logout") {
      const currentUserRole = role;
      await dispatch(logout({ fcmToken: getFcmToken() }))?.unwrap();
      const target =
        currentUserRole && currentUserRole.toLowerCase() === "fundraiser"
          ? "/fundraiser/account-login"
          : "/";
      navigate(target);
      return;
    }

    if (pendingAction === "reload") {
      window.location.reload();
      return;
    }

    navigate(pendingNavigatePath || offersPath);
  }, [
    dispatch,
    navigate,
    offersPath,
    pendingAction,
    pendingNavigatePath,
    role,
  ]);

  const getAdminSaveExitMessage = useCallback(() => {
    if (!isAdminRole) return null;

    const currentStatus = String(
      editOfferData?.status || offerData?.status || "",
    ).toLowerCase();

    if (currentStatus === "live" || currentStatus === "approved") {
      return "Your offer changes are now live.";
    }

    if (currentStatus === "pending") {
      return "Your offer changes have been saved and the offer remains pending review.";
    }

    return null;
  }, [editOfferData?.status, isAdminRole, offerData?.status]);

  const getPreparedExitSaveContext = useCallback(() => {
    const currentOfferId = id || offerData?._id || editOfferData?._id;

    const comparisonPayloadInput = { ...(payload || {}) };
    const safePayload = { ...comparisonPayloadInput };
    delete safePayload.ruleAndRegulationForComparison;
    delete safePayload.rulesStepTouched;
    delete safePayload.preserveCurrentStatus;
    delete safePayload.forceResubmit;
    delete safePayload.status;
    delete safePayload.isCreated;

    const effectiveMerchantId =
      selectedMerchant?._id ||
      editOfferData?.merchant?._id ||
      editOfferData?.merchantId ||
      offerData?.merchant?._id ||
      offerData?.merchantId ||
      (typeof editOfferData?.merchant === "string"
        ? editOfferData.merchant
        : null) ||
      (typeof offerData?.merchant === "string" ? offerData.merchant : null) ||
      null;

    const shouldBeCreated =
      payload?.isCreated !== undefined
        ? payload.isCreated
        : (editOfferData?.isCreated ?? offerData?.isCreated ?? false);
    const originalOffer = editOfferData || offerData;
    const comparisonPayload = {
      ...(originalOffer || {}),
      ...(offerData || {}),
      ...comparisonPayloadInput,
    };

    const exitBehavior = getOfferExitBehavior({
      currentPayload: comparisonPayload,
      originalOffer,
      isLive: isEdit && isApproved,
      role,
      isEdit,
      currentStatus: editOfferData?.status || offerData?.status,
      isLeaveSaveStep,
      fallbackIsCreated: shouldBeCreated,
      fallbackStatus: isApproved ? "live" : null,
    });
    const progressSaveFlags = getOfferEditProgressSaveFlags({
      role,
      isEdit,
      editOfferData,
      offerData,
      isApprovedProp: isApproved,
    });
    return {
      currentOfferId,
      safePayload,
      effectiveMerchantId,
      exitBehavior,
      progressSaveFlags,
    };
  }, [
    editOfferData,
    id,
    isApproved,
    isEdit,
    isLeaveSaveStep,
    offerData,
    payload,
    role,
    selectedMerchant?._id,
  ]);

  // Client-side pre-validation: check explicitly-set payload fields before
  // hitting the API. Using hasOwnProperty ensures we only validate fields that
  // were actually touched (visited) — if a key isn't in the payload at all the
  // user never went through that step, so we skip it.
  const validatePayloadBeforeExit = useCallback(
    (safePayload) => {
      if (!safePayload) return null;

      const hasOwn = (key) =>
        Object.prototype.hasOwnProperty.call(safePayload, key);
      const hasAny = (keys) => keys.some((key) => hasOwn(key));
      const toTrimmedString = (value) => String(value || "").trim();
      const isStrictUrl = (value) => {
        try {
          if (!value) return false;
          new URL(String(value).trim());
          return true;
        } catch (_error) {
          return false;
        }
      };
      const extractMainDescription = () => {
        const rawDescription = String(safePayload.description || "");
        const normalized = rawDescription.replace(/\r\n/g, "\n");
        const detailSeparator = "||DETAIL||";

        if (normalized.includes(detailSeparator)) {
          return normalized
            .slice(0, normalized.indexOf(detailSeparator))
            .trim();
        }

        if (normalized.includes("\n\n")) {
          const parts = normalized.split(/\n\n+/);
          const details = parts.pop() || "";
          const main = parts.join("\n").trim();
          if (main) return main;
          if (details) return "";
        }

        const detailOnlyValue = [
          safePayload.freeOfferDetails,
          safePayload.bogoDetails,
          safePayload.bundleOfferDetails,
        ]
          .map((value) => toTrimmedString(value))
          .find(Boolean);

        if (detailOnlyValue && normalized.trim() === detailOnlyValue) {
          return "";
        }

        return normalized.trim();
      };

      if (hasOwn("category") && !safePayload.category) {
        return {
          stepIndex: getStepNumber(1),
          field: "category",
          message: "Category is required",
        };
      }

      if (
        hasOwn("subCategory") &&
        (!safePayload.subCategory ||
          (Array.isArray(safePayload.subCategory) &&
            safePayload.subCategory.length === 0))
      ) {
        return {
          stepIndex: getStepNumber(2),
          field: "subCategory",
          message: "Sub-category is required",
        };
      }

      const hasAboutOfferData = hasAny([
        "title",
        "offerType",
        "offerDiscountType",
        "description",
        "thumbnailImage",
        "previewImages",
        "websiteURL",
        "onlineOption",
        "offerCodes",
        "discountRange",
        "fixedDollarAmount",
        "freeOfferDetails",
        "bogoDetails",
        "bundleOfferDetails",
      ]);

      if (hasAboutOfferData) {
        const offerType = safePayload.offerType;
        const offerDiscountType = safePayload.offerDiscountType;
        const previewImages = Array.isArray(safePayload.previewImages)
          ? safePayload.previewImages.filter(Boolean)
          : [];
        const offerCodes = Array.isArray(safePayload.offerCodes)
          ? safePayload.offerCodes.filter(Boolean)
          : [];
        const mainDescription = extractMainDescription();

        if (!toTrimmedString(safePayload.title)) {
          return {
            stepIndex: getStepNumber(3),
            field: "title",
            message: "Offer title is required",
          };
        }

        if (!toTrimmedString(offerType)) {
          return {
            stepIndex: getStepNumber(3),
            field: "offerType",
            message: "Offer Type Selection is required",
          };
        }

        if (!toTrimmedString(offerDiscountType)) {
          return {
            stepIndex: getStepNumber(3),
            field: "offerDiscountType",
            message: "Offer Discount Type Selection is required",
          };
        }

        if (!mainDescription) {
          return {
            stepIndex: getStepNumber(3),
            field: "description",
            message: "Offer Description is required",
          };
        }

        if (!safePayload.thumbnailImage) {
          return {
            stepIndex: getStepNumber(3),
            field: "thumbnailImage",
            message: "At least one thumbnail image is required",
          };
        }

        if (previewImages.length === 0) {
          return {
            stepIndex: getStepNumber(3),
            field: "previewImages",
            message: "At least one preview image is required",
          };
        }

        if (offerType === "ONLINE_OFFER") {
          if (!toTrimmedString(safePayload.onlineOption)) {
            return {
              stepIndex: getStepNumber(3),
              field: "onlineOption",
              message: "Please select an option",
            };
          }

          if (!isStrictUrl(safePayload.websiteURL)) {
            return {
              stepIndex: getStepNumber(3),
              field: "websiteURL",
              message:
                "Please enter a valid website URL including http:// or https://",
            };
          }

          if (safePayload.onlineOption === "ONLINE_WITH_CODE") {
            if (offerCodes.length === 0) {
              return {
                stepIndex: getStepNumber(3),
                field: "offerCodes",
                message: "At least one code is required",
              };
            }

            const invalidCode = offerCodes.find(
              (code) => !/^[a-zA-Z0-9]{6}$/.test(String(code)),
            );
            if (invalidCode) {
              return {
                stepIndex: getStepNumber(3),
                field: "offerCodes",
                message: "Each code must be exactly 6 alphanumeric characters",
              };
            }
          }
        }

        if (offerDiscountType === "DISCOUNTED_PERCENT") {
          const discountRange = Number(safePayload.discountRange);
          if (
            !Number.isFinite(discountRange) ||
            discountRange < 0 ||
            discountRange > 100
          ) {
            return {
              stepIndex: getStepNumber(3),
              field: "discountRange",
              message: "Range is required",
            };
          }
        }

        if (offerDiscountType === "FIXED_DOLLAR") {
          const fixedDollarAmount = Number(safePayload.fixedDollarAmount);
          if (!Number.isFinite(fixedDollarAmount) || fixedDollarAmount <= 0) {
            return {
              stepIndex: getStepNumber(3),
              field: "fixedDollarAmount",
              message: "Fixed Dollar Amount is required",
            };
          }
        }

        if (
          offerDiscountType === "FREE_OFFER" &&
          !toTrimmedString(safePayload.freeOfferDetails)
        ) {
          return {
            stepIndex: getStepNumber(3),
            field: "freeOfferDetails",
            message: "Free offer details are required",
          };
        }

        if (
          offerDiscountType === "BUY_ONE_GET_ONE_FREE" &&
          !toTrimmedString(safePayload.bogoDetails)
        ) {
          return {
            stepIndex: getStepNumber(3),
            field: "bogoDetails",
            message: "BOGO details are required",
          };
        }

        if (
          offerDiscountType === "BUNDLE_OFFER" &&
          !toTrimmedString(safePayload.bundleOfferDetails)
        ) {
          return {
            stepIndex: getStepNumber(3),
            field: "bundleOfferDetails",
            message: "Bundle details are required",
          };
        }
      }

      const hasCustomizeData = hasAny([
        "redemptionMethod",
        "necessaryRule",
        "redemptionFrequency",
        "startDate",
        "endDate",
        "startTime",
        "endTime",
        "redemptionDays",
        "callback_url",
      ]);

      if (hasCustomizeData) {
        const necessaryRule = safePayload.necessaryRule;
        const redemptionFrequency = safePayload.redemptionFrequency;
        const redemptionDays = Array.isArray(safePayload.redemptionDays)
          ? safePayload.redemptionDays.filter(Boolean)
          : [];

        if (!toTrimmedString(safePayload.redemptionMethod)) {
          return {
            stepIndex: getStepNumber(4),
            field: "redemptionMethod",
            message: "At least one redemption method is required.",
          };
        }

        if (!toTrimmedString(necessaryRule)) {
          return {
            stepIndex: getStepNumber(4),
            field: "rules",
            message: "Necessary Rules are required",
          };
        }

        if (
          safePayload.callback_url &&
          !isStrictUrl(safePayload.callback_url)
        ) {
          return {
            stepIndex: getStepNumber(4),
            field: "url",
            message: "Provided callback URL is invalid.",
          };
        }

        const requiresFrequency =
          necessaryRule === "REDEEM_ONCE" ||
          necessaryRule === "OFFER_AVAILABLE_THROUGHOUT_YEAR";

        if (requiresFrequency && !toTrimmedString(redemptionFrequency)) {
          return {
            stepIndex: getStepNumber(4),
            field: "rules",
            message: "Redemption Frequency is required.",
          };
        }

        const requiresDateRange =
          (necessaryRule === "REDEEM_ONCE" &&
            [
              "DAILY",
              "IN_A_WEEK",
              "EVERY_30_DAYS",
              "EVERY_90_DAYS",
              "EVERY_120_DAYS",
            ].includes(redemptionFrequency)) ||
          (necessaryRule === "OFFER_AVAILABLE_THROUGHOUT_YEAR" &&
            ["IN_A_WEEK", "EVERY_30_DAYS", "ONCE_IN_12_MONTHS"].includes(
              redemptionFrequency,
            )) ||
          necessaryRule === "CHOOSE_DATE_RANGE";

        if (requiresDateRange && !safePayload.startDate) {
          return {
            stepIndex: getStepNumber(4),
            field: "startDate",
            message: "Start date is required",
          };
        }

        if (requiresDateRange && !safePayload.endDate) {
          return {
            stepIndex: getStepNumber(4),
            field: "endDate",
            message: "End date is required",
          };
        }

        const requiresDays =
          (necessaryRule === "REDEEM_ONCE" &&
            [
              "IN_A_WEEK",
              "EVERY_30_DAYS",
              "EVERY_90_DAYS",
              "EVERY_120_DAYS",
            ].includes(redemptionFrequency)) ||
          (necessaryRule === "OFFER_AVAILABLE_THROUGHOUT_YEAR" &&
            ["IN_A_WEEK", "EVERY_30_DAYS", "ONCE_IN_12_MONTHS"].includes(
              redemptionFrequency,
            ));

        if (requiresDays && redemptionDays.length === 0) {
          return {
            stepIndex: getStepNumber(4),
            field: "days",
            message: "Please select at least one day",
          };
        }
      }

      if (
        hasOwn("ruleAndRegulation") &&
        (!Array.isArray(safePayload.ruleAndRegulation) ||
          safePayload.ruleAndRegulation.filter(Boolean).length === 0)
      ) {
        return {
          stepIndex: getStepNumber(5),
          field: "ruleAndRegulation",
          message: "Please select at least one Rule and Regulation",
        };
      }

      const requiresLocations =
        safePayload.offerType === "INSTORE_SWOODLE_OFFER" ||
        safePayload.offerType === "INSTORE_OFFER";

      if (
        requiresLocations &&
        hasAny(["locations", "availableAcrossAllStores"]) &&
        !safePayload.availableAcrossAllStores &&
        (!Array.isArray(safePayload.locations) ||
          safePayload.locations.filter(Boolean).length === 0)
      ) {
        return {
          stepIndex: getStepNumber(6),
          field: "location",
          message: "At least one location is required",
        };
      }

      if (
        hasOwn("title") &&
        (!safePayload.title || String(safePayload.title).trim() === "")
      ) {
        return {
          stepIndex: getStepNumber(3),
          field: "title",
          message: "Offer title is required",
        };
      }

      if (hasOwn("offerDiscountType") && !safePayload.offerDiscountType) {
        return {
          stepIndex: getStepNumber(3),
          field: "offerDiscountType",
          message: "Discount type is required",
        };
      }

      return null;
    },
    [getStepNumber],
  );

  const saveOfferBeforeExit = useCallback(
    async ({ forceResubmit = false } = {}) => {
      if (payload === null) {
        return true;
      }

      try {
        const {
          currentOfferId,
          safePayload,
          effectiveMerchantId,
          exitBehavior,
          progressSaveFlags,
        } = getPreparedExitSaveContext();

        if (
          (currentOfferId || safePayload.title) &&
          Object.keys(safePayload).length > 0
        ) {
          const requestPayload = {
            ...safePayload,
            offerId: currentOfferId,
            ...(effectiveMerchantId && { merchantId: effectiveMerchantId }),
          };

          if (requestPayload.necessaryRule === "CHOOSE_DATE_RANGE") {
            delete requestPayload.redemptionFrequency;
          }

          const clientIssue = validatePayloadBeforeExit(safePayload);
          if (clientIssue) {
            setPendingNavigatePath(null);
            setPendingAction("navigate");
            setShowExitResubmitConfirm(false);
            setExitValidationIssue(clientIssue);
            setStepIndex(clientIssue.stepIndex);
            toast.error(clientIssue.message);
            return false;
          }

          if (forceResubmit) {
            await dispatch(
              createOffer({
                ...requestPayload,
                isCreated: true,
                forceResubmit: true,
              }),
            ).unwrap();
            toast.success("Offer resubmitted successfully");
          } else {
            if (progressSaveFlags.preserveCurrentStatus) {
              // Merchant edit path
              requestPayload.preserveCurrentStatus = true;
              requestPayload.isCreated = progressSaveFlags.isCreated;
              if (progressSaveFlags.status) {
                requestPayload.status = progressSaveFlags.status;
              }
            } else if (exitBehavior.mode === "save_live") {
              // Merchant live safe-edit path
              requestPayload.isCreated = true;
              requestPayload.status = "live";
            } else {
              // Fix 1: Admin edit path.
              // Previously neither merchant branch matched for admin, so the
              // request went out without isCreated or status and the backend
              // treated the save as a draft (isCreated:false).
              // progressSaveFlags already computes the correct values:
              //   live offer  → { isCreated: true,  status: "live" }
              //   all others  → { isCreated: false }
              requestPayload.isCreated = progressSaveFlags.isCreated;
              if (progressSaveFlags.status) {
                requestPayload.status = progressSaveFlags.status;
              }
            }

            await dispatch(createOffer(requestPayload)).unwrap();
            const toastMsg =
              exitBehavior.mode === "save_live"
                ? "Changes saved successfully"
                : progressSaveFlags.isCreated
                  ? "Changes saved successfully"
                  : "Progress saved successfully";
            toast.success(toastMsg);
          }
        }

        return true;
      } catch (error) {
        console.error("Save progress error:", error);
        const message =
          error?.message ||
          error?.data?.message ||
          "We couldn't save your changes just yet. Please try again.";
        const validationIssue = getExitValidationIssue(message);

        if (validationIssue) {
          setPendingNavigatePath(null);
          setPendingAction("navigate");
          setShowExitResubmitConfirm(false);
          setExitValidationIssue(validationIssue);
          setStepIndex(validationIssue.stepIndex);
        }

        toast.error(message);
        return false;
      }
    },
    [
      dispatch,
      getExitValidationIssue,
      getPreparedExitSaveContext,
      payload,
      validatePayloadBeforeExit,
    ],
  );

  const handleSaveAndExit = useCallback(async () => {
    clearExitValidationIssue();
    const shouldShowAdminModal = Boolean(getAdminSaveExitMessage());
    if (shouldShowAdminModal) {
      setIsAdminSaveAndExitSubmitting(true);
    }
    try {
      const didSave = await saveOfferBeforeExit();
      if (didSave) {
        const adminSuccessMessage = getAdminSaveExitMessage();
        if (adminSuccessMessage) {
          setPendingAction("navigate");
          setPendingNavigatePath(offersPath);
          setAdminSaveSuccessMessage(adminSuccessMessage);
          setShowAdminSaveSuccessModal(true);
          return;
        }

        navigate(offersPath);
      }
    } finally {
      setIsAdminSaveAndExitSubmitting(false);
    }
  }, [
    clearExitValidationIssue,
    getAdminSaveExitMessage,
    navigate,
    offersPath,
    saveOfferBeforeExit,
  ]);

  const handleDiscardChangesAndExit = useCallback(async () => {
    clearExitValidationIssue();
    setShowLeaveConfirm(false);
    setShowExitResubmitConfirm(false);
    await completePendingExit();
  }, [clearExitValidationIssue, completePendingExit]);

  const handleLeaveDialogSaveAndExit = useCallback(async () => {
    const { exitBehavior } = getPreparedExitSaveContext();

    setShowLeaveConfirm(false);
    clearExitValidationIssue();

    // B3 fix: Previously this was gated on isLeaveSaveStep (stepIndex >= 3).
    // That caused data loss when the user navigated back to an early step AFTER
    // completing later steps — the later-step payload was in `payload` state but
    // saveOfferBeforeExit was never called. Now we save whenever payload has data,
    // regardless of which step is currently shown on screen.
    const hasPayloadData =
      payload !== null && Object.keys(payload || {}).length > 0;
    if (!hasPayloadData) {
      await completePendingExit();
      return;
    }

    if (exitBehavior.requiresResubmit) {
      setShowExitResubmitConfirm(true);
      return;
    }

    const shouldShowAdminModal = Boolean(getAdminSaveExitMessage());
    const shouldShowMerchantModal = shouldShowMerchantBrowserBackProcessing;
    if (shouldShowAdminModal) {
      setIsAdminSaveAndExitSubmitting(true);
    }
    if (shouldShowMerchantModal) {
      setIsMerchantExitSubmitting(true);
    }
    try {
      const didSave = await saveOfferBeforeExit();
      if (didSave) {
        const adminSuccessMessage = getAdminSaveExitMessage();
        if (adminSuccessMessage) {
          setAdminSaveSuccessMessage(adminSuccessMessage);
          setShowAdminSaveSuccessModal(true);
          return;
        }

        await completePendingExit();
      }
    } finally {
      setIsAdminSaveAndExitSubmitting(false);
      setIsMerchantExitSubmitting(false);
    }
  }, [
    clearExitValidationIssue,
    completePendingExit,
    getAdminSaveExitMessage,
    getPreparedExitSaveContext,
    payload,
    saveOfferBeforeExit,
    shouldShowMerchantBrowserBackProcessing,
  ]);

  const handleCloseAdminSaveSuccessModal = useCallback(async () => {
    setShowAdminSaveSuccessModal(false);
    await completePendingExit();
  }, [completePendingExit]);

  const handleExitResubmit = useCallback(async () => {
    clearExitValidationIssue();
    setIsExitResubmitting(true);
    if (shouldShowMerchantBrowserBackProcessing) {
      setIsMerchantExitSubmitting(true);
    }
    try {
      const didSave = await saveOfferBeforeExit({ forceResubmit: true });
      if (!didSave) {
        return;
      }

      setShowExitResubmitConfirm(false);
      await completePendingExit();
    } finally {
      setIsExitResubmitting(false);
      setIsMerchantExitSubmitting(false);
    }
  }, [
    clearExitValidationIssue,
    completePendingExit,
    saveOfferBeforeExit,
    shouldShowMerchantBrowserBackProcessing,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    // On mount, if we are creating a new offer, ensure state is clean
    if (!id) {
      dispatch(resetOfferState());
      setPayload(null);
      setCategoryId(undefined);
    }
    return () => {
      dispatch(resetOfferState());
    };
  }, [id, dispatch]);

  useEffect(() => {
    // Explicitly scroll the document to the very top to fix iOS/Safari/Chrome bugs on step transitions
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;

    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [stepIndex]);

  // Reset offer state & local selections when merchant changes to prevent stale data or permission issues
  useEffect(() => {
    if (
      selectedMerchant &&
      offerData?.merchant?._id &&
      offerData?.merchant?._id !== selectedMerchant._id
    ) {
      dispatch(resetOfferState());
      setCategoryId(undefined);
      setSubCategories([]);
    }
  }, [selectedMerchant, offerData?.merchant?._id, dispatch]);

  // Guard (admin any, merchant create/edit): intercept back button and mark session for sidebar
  useEffect(() => {
    const shouldGuard = role === "admin" || role === "merchant";
    if (!shouldGuard) return;

    // Mark a session flag for sidebar/navigation interception
    sessionStorage.setItem("ADMIN_OFFER_CREATION_GUARD", "true");

    const handlePopState = () => {
      // push back to current page and show modal
      window.history.pushState(null, "", window.location.pathname);
      // B2 fix: Do NOT clear exitValidationIssue here.
      // Clearing it wiped inline field errors that were already displaying on the
      // current step when the user pressed Back a second time. We only clear the
      // issue once the Leave dialog resolves (discard/save branches handle it).
      //
      // B7 fix: Read offersPath from the store at call-time instead of relying
      // on the closure value which may be stale if role resolved after this
      // effect first mounted (role starts as null, resolves async).
      let dynamicOffersPath = offersPath;
      try {
        const currentRole =
          store.getState()?.authReducer?.tokenData?.user?.role;
        dynamicOffersPath =
          currentRole === "admin"
            ? "/admin/offer-list"
            : "/merchant/offer-list";
      } catch (_) {
        /* keep closure fallback */
      }
      setShowLeaveConfirm(true);
      setPendingNavigatePath(dynamicOffersPath);
    };

    const handleAttemptExit = (evt) => {
      const targetPath = evt?.detail?.path || offersPath;
      setPendingNavigatePath(targetPath);
      if (evt?.detail?.action) {
        setPendingAction(evt.detail.action);
      } else {
        setPendingAction("navigate");
      }
      setExitValidationIssue(null);
      setShowLeaveConfirm(true);
    };

    // Do not attach native beforeunload to avoid browser toast
    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("attempt-exit-offer-form", handleAttemptExit);

    // Intercept refresh shortcuts (F5, Ctrl/Cmd+R) to show custom modal
    const keydownHandler = (e) => {
      const isRefreshKey =
        e.key === "F5" ||
        e.keyCode === 116 ||
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "r");
      if (isRefreshKey) {
        e.preventDefault();
        setPendingAction("reload");
        setExitValidationIssue(null);
        setShowLeaveConfirm(true);
      }
    };
    window.addEventListener("keydown", keydownHandler, true);

    // Intercept Link (<a>) navigations globally
    const clickCapture = (e) => {
      const anchor = e.target.closest && e.target.closest("a[href]");
      if (!anchor) return;

      try {
        const href = anchor.getAttribute("href") || "";
        const hasDownload = anchor.hasAttribute("download");
        const bypass = anchor.getAttribute("data-bypass-exit-guard") === "true";

        // Allow downloads and blob/object URLs without guard
        if (hasDownload || href.startsWith("blob:") || bypass) {
          return; // don't intercept
        }

        // Intercept in-app navigations only
        if (!href.startsWith("http")) {
          e.preventDefault();
          setPendingNavigatePath(href);
          setExitValidationIssue(null);
          setShowLeaveConfirm(true);
        }
      } catch (_) {}
    };
    document.addEventListener("click", clickCapture, true);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("attempt-exit-offer-form", handleAttemptExit);
      window.removeEventListener("keydown", keydownHandler, true);
      document.removeEventListener("click", clickCapture, true);
      sessionStorage.removeItem("ADMIN_OFFER_CREATION_GUARD");
    };
  }, [role, isEdit]);

  if (shouldHoldAdminEditBootstrap) {
    return (
      <div className="offerFormPage flex min-h-[60vh] items-center justify-center">
        <p className="font-poppins text-[14px] text-[#767676]">
          Loading offer details...
        </p>
      </div>
    );
  }

  return (
    <div ref={topRef} className="offerFormPage">
      {/* Step 1: Select Merchant (Admin only) */}
      {role === "admin" && stepIndex === 1 && (
        <OfferFormLayout
          content={
            <MerchantSelection
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              selectedMerchant={selectedMerchant}
              setSelectedMerchant={setSelectedMerchant}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              isApproved={isApproved}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          handlePrevious={handlePrevious}
          showPrevious={false}
          showNext={true}
        />
      )}

      {/* Category Selection */}
      {stepIndex === getStepNumber(1) && (
        <OfferFormLayout
          content={
            <CategorySelection
              setSubCategories={setSubCategories}
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              categoryId={categoryId}
              setCategoryId={setCategoryId}
              setPayloadData={setPayloadData}
              categories={categories}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
              payload={payload}
              isApproved={isApproved}
              validationIssue={exitValidationIssue}
              onValidationIssueHandled={clearExitValidationIssue}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={
            !(
              merchant === true &&
              role !== "admin" &&
              stepIndex === getStepNumber(1)
            )
          }
        />
      )}

      {/* Sub Category Selection */}
      {stepIndex === getStepNumber(2) && (
        <OfferFormLayout
          content={
            <SubCategorySelection
              categoryId={categoryId}
              subCategories={subCategories}
              setSubCategories={setSubCategories}
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              categories={categories}
              handleSaveAndExit={handleSaveAndExit}
              payload={payload}
              isApproved={isApproved}
              validationIssue={exitValidationIssue}
              onValidationIssueHandled={clearExitValidationIssue}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}

      {/* Offer Creation */}
      {stepIndex === getStepNumber(3) && (
        <OfferFormLayout
          content={
            <OfferCreation
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
              payload={payload}
              isApproved={isApproved}
              validationIssue={exitValidationIssue}
              onValidationIssueHandled={clearExitValidationIssue}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}

      {/* Customize Offer */}
      {stepIndex === getStepNumber(4) && (
        <OfferFormLayout
          content={
            <CustomizeOffer
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
              payload={payload}
              isApproved={isApproved}
              validationIssue={exitValidationIssue}
              onValidationIssueHandled={clearExitValidationIssue}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}

      {/* Rules and Regulations */}
      {stepIndex === getStepNumber(5) && (
        <OfferFormLayout
          content={
            <RulesRegulations
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
              payload={payload}
              isApproved={isApproved}
              validationIssue={exitValidationIssue}
              onValidationIssueHandled={clearExitValidationIssue}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}

      {/* Location (for INSTORE_SWOODLE_OFFER only) */}
      {stepIndex === getStepNumber(6) &&
        offerData?.offerType === "INSTORE_SWOODLE_OFFER" && (
          <OfferFormLayout
            content={
              <Location
                stepIndex={stepIndex}
                setStepIndex={setStepIndex}
                handlePrevious={handlePrevious}
                setPayloadData={setPayloadData}
                editOfferData={editOfferData}
                isEdit={isEdit}
                selectedMerchant={selectedMerchant}
                handleSaveAndExit={handleSaveAndExit}
                payload={payload}
                isApproved={isApproved}
                validationIssue={exitValidationIssue}
                onValidationIssueHandled={clearExitValidationIssue}
              />
            }
            stepIndex={stepIndex}
            handleSaveAndExit={handleSaveAndExit}
            onStepClick={handleStepClick}
            handlePrevious={handlePrevious}
            showPrevious={true}
          />
        )}

      {/* Review and Save */}
      {((stepIndex === getStepNumber(7) &&
        offerData?.offerType === "INSTORE_SWOODLE_OFFER") ||
        (stepIndex === getStepNumber(6) &&
          offerData?.offerType !== "INSTORE_SWOODLE_OFFER")) && (
        <OfferFormLayout
          content={
            <OfferReview
              handlePrevious={handlePrevious}
              editOfferData={editOfferData}
              setPayloadData={setPayloadData}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
              payload={payload}
              isApproved={isApproved}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}
      {showLeaveConfirm && (
        <ThreeActionPopup
          icon={InfoIcon}
          content={
            isLeaveSaveStep
              ? "Are you sure you want to exit this offer? Choose whether to save your changes or discard them before leaving."
              : "Are you sure you want to exit this offer?"
          }
          popupClassName="sm:w-[620px]"
          firstActionClassName="sm:flex-[0.95]"
          secondActionClassName="sm:flex-[1.45]"
          thirdActionClassName="sm:flex-[1.1]"
          firstAction={
            <PrimaryOutlineButton
              label="No, Stay"
              className="w-full px-4 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[44px] rounded-[8px] transition-all hover:bg-[#F0FBFF]"
              onClick={() => setShowLeaveConfirm(false)}
            />
          }
          secondAction={
            <button
              type="button"
              className="w-full bg-white border border-[#FF5A5F] text-[#FF5A5F] h-[44px] rounded-[8px] font-poppins text-[13px] md:text-[14px] font-[500] transition-all hover:bg-[#FFF3F3]"
              onClick={handleDiscardChangesAndExit}
            >
              Discard Changes and Exit
            </button>
          }
          thirdAction={
            <PrimaryButton
              label="Save and Exit"
              className="w-full px-4 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[44px] rounded-[8px] transition-all shadow-sm bg-[#00ADEF] hover:opacity-90 text-white"
              onClick={handleLeaveDialogSaveAndExit}
            />
          }
        />
      )}
      {isAdminSaveAndExitSubmitting &&
        !showAdminSaveSuccessModal &&
        createPortal(
          <div className="offer-processing-backdrop">
            <div className="offer-processing-card">
              <h3>Saving Offer...</h3>
              <p>Please wait while we save your changes.</p>
            </div>
          </div>,
          document.body,
        )}
      {isMerchantExitSubmitting &&
        createPortal(
          <div className="offer-processing-backdrop">
            <div className="offer-processing-card">
              <h3>Saving Offer...</h3>
              <p>Please wait while we save your changes.</p>
            </div>
          </div>,
          document.body,
        )}
      {showAdminSaveSuccessModal &&
        createPortal(
          <div
            className="offer-success-modal-backdrop"
            onClick={handleCloseAdminSaveSuccessModal}
          >
            <div
              className="offer-success-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Offer Submitted!</h3>
              <p className="offer-success-message">{adminSaveSuccessMessage}</p>
              <div className="offer-success-actions">
                <PrimaryButton
                  className="offer-success-ok-btn w-[60%] h-[45px] rounded-[8px] text-[16px] font-[500] font-montserrat"
                  label="OK"
                  onClick={handleCloseAdminSaveSuccessModal}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
      {showExitResubmitConfirm && (
        <GenericPopup
          icon={InfoIcon}
          content="Your updated offer will be sent to admin for approval. Do you want to resubmit it now?"
          popupClassName="w-[335px] sm:w-[470px] pb-8"
          cancelBtn={
            <PrimaryOutlineButton
              label="Cancel"
              className="px-6 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[40px] md:h-[42px] rounded-[8px] mt-6 transition-all hover:bg-[#F0FBFF]"
              disabled={isExitResubmitting}
              onClick={() => setShowExitResubmitConfirm(false)}
            />
          }
          continueBtn={
            <PrimaryButton
              label={isExitResubmitting ? "Processing..." : "Resubmit"}
              loading={isExitResubmitting}
              className="px-6 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[40px] md:h-[42px] rounded-[8px] mt-6 transition-all shadow-sm bg-[#00ADEF] hover:opacity-90 text-white"
              onClick={handleExitResubmit}
            />
          }
        />
      )}
    </div>
  );
};

export default OfferForm;
